/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : UpdateUser.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 07-07-2024
 * @description : This page is to update the user details.
 */



import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  DialogContentText,
  IconButton,
  Stack,
  Avatar,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import DefaultProfilePic from "../../assets/logoIcons/defaultProfilePic.png";
import { toast } from "react-toastify";
import {
  GetUserPicAction,
  HandleApiActions,
  profileActions,
  UploadUserPicAction,
  userManagementApis,
} from "../../redux/actions";
import roletypeActions from "../../redux/actions/roletypeActions";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import {
  CustomConfirmationDialog,
  PhoneNumberTextField,
} from "../../components";

import appTheme from "../../assets/AppTheme/appTheme";
import customConsole from "../../config/customConsole";
import CustomFormTextFieldV3 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV3";
import CustomSelectV3 from "../../components/customComponentsV2/common/select/CustomSelectV3";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 07-07-2024
 * @description : Component to update the user details.
 * @param : userData, toggleDrawer
 * @return : The rendered update user component.
 */
const UpdateUser = ({
  userData,
  setOpenDrawer,
  handleGetUsersList,
  setOpenUserDeleteDialog,
  openUserDeleteDialog,
}) => {
  console.log("userData: ", userData);
  const dispatch = useDispatch();
  const [fullName, setFullName] = useState(userData?.fullname || "");
  const [fullNameError, setFullNameError] = useState("");
  const [loggedInUserDetails, setLoggedInUserDetails] = useState("");
  const [email, setEmail] = useState(userData?.email || "");
  const [emailError, setEmailError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(userData?.phone || "");
  const [phoneNumberError, setPhoneNumberError] = useState("");

  const [phoneCodeOptions, setPhoneCodeOptions] = useState([]);
  const [selectedRoleDepartmentId, setSelectedRoleDepartmentId] = useState(
    userData?.role_id ? String(userData.role_id) : Number(userData.role_id),
  );

  const [selectedPendingRoleDepartmentId, setSelectedPendingRoleDepartmentId] =
    useState(
      userData?.role_details?.role_id
        ? String(userData.role_details.role_id)
        : Number(userData.role_details.role_id),
    );
  const [roleDepartmentOptions, setRoleDepartmentOptions] = useState([]);


  const [selectedPhoneCode, setSelectedPhoneCode] = useState(
    userData?.phone_code || "",
  );
  const [regionOptions, setRegionOptions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(userData?.region || "");
  const [isUserActive, setIsUserActive] = useState(
    userData?.is_active || false,
  );
  const [profilePicName, setProfilePicName] = useState(
    userData?.profile?.profile_pic || "",
  );
  const [profilePicFilePath, setProfilePicFilePath] = useState("");
  const [assignedRolesOptions, setAssignedRolesOptions] = useState([]);
  const [selectedAssignedRole, setSelectedAssignedRole] = useState("");
  const [selectedAssignedRoleName, setSelectedAssignedRoleName] = useState("");
  const [projectAccessOptions, setProjectAccessOptions] = useState([]);
  const [selectedProjectAccess, setSelectedProjectAccess] = useState("");

  const [openProfilePicDialog, setOpenProfilePicDialog] = useState(false);
  const { countryCodes } = useSelector(
    (state) => state.userManagementReducer,
    shallowEqual,
  );

  const { rolesList } = useSelector(
    (state) => state.userManagementReducer,
    shallowEqual,
  );
  const roleTypesAll = useSelector(
    (state) => state.roleTypeReducers.roleTypesAll,
  );

  useEffect(() => {
    if (!roleTypesAll?.data?.roleTypes?.length) {
      dispatch(
        HandleApiActions({
          ...roletypeActions.get_all_roletype,
          show_toast: false,
        }),
      );
    }
  }, [dispatch, roleTypesAll]);
  const [rolesOptions, setRolesOptions] = useState([]);
  const profileDetails = useSelector((store) => store.profileReducer);
  customConsole("profileDetails: " + profileDetails);

  const [openUserStatusDialog, setOpenUserStatusDialog] = useState(false);
  const [pendingUserStatus, setPendingUserStatus] = useState(isUserActive);

  const [
    openUserUpdateRoleDepartmentDialog,
    setOpenUserUpdateRoleDepartmentDialog,
  ] = useState(false);

  const [selectedRoleType, setSelectedRoleType] = useState(
    userData?.role_details?.role_type || "",
  );
  const [selectedRoleTypeName, setSelectedRoleTypeName] = useState(
    userData?.role_details?.role_type_name || "",
  );
  const [selectedProfilePic, setSelectedProfilePic] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const roleTypeOptions = roleTypesAll?.data?.roleTypes
    ?.map((roleType) => ({
      value: roleType?.role_type_id,
      label: roleType?.display_name,
      name: roleType?.name,
    }))
    ?.sort((a, b) => (a?.label || "").localeCompare(b?.label || ""));

  const handleUserStatusChange = (newStatus) => {
    setPendingUserStatus(newStatus);
    setOpenUserStatusDialog(true);
  };

  useEffect(() => {
    if (!roleTypeOptions?.length) return;

    const roleTypeName = userData?.role_details?.role_type_name;
    const roleTypeId = userData?.role_details?.role_type_id;

    const matchedOption = roleTypeOptions?.find((option) => {
      if (roleTypeId) {
        return String(option?.value) === String(roleTypeId);
      }
      return String(option?.name) === String(roleTypeName);
    });

    if (matchedOption) {
      setSelectedRoleType(matchedOption.value);
      setSelectedRoleTypeName(matchedOption.name || "");
    }
  }, [roleTypeOptions, userData]);

  useEffect(() => {
    console.log(
      "selectedRoleTypeselectedRoleTypeselectedRoleType",
      selectedRoleType,
    );
    console.log(
      "selectedRoleTypeNameselectedRoleTypeNameselectedRoleTypeName",
      selectedRoleTypeName,
    );
    const fetchRolesByRoleTypeName = async () => {
      // if (!selectedRoleTypeName) {
      //   setRoleDepartmentOptions([]);
      //   return;
      // }

      const resp = await dispatch(
        HandleApiActions({
          ...userManagementApis.list_role_ddh_ucm,
          params: {
            roleTypeName: selectedRoleTypeName,
            limit: 100,
            offset: 1,
          },
          show_toast: false,
        }),
      );

      const roles = resp?.data?.roles || [];
      const mappedRoles = roles
        ?.filter((role) => role?.is_active)
        ?.map((role) => ({
          value: role?.role_id,
          label: role?.name,
        }))
        ?.sort((a, b) => (a?.label || "").localeCompare(b?.label || ""));

      setRoleDepartmentOptions(mappedRoles);
    };
    if (selectedRoleType) {
      fetchRolesByRoleTypeName();
    }
  }, [selectedRoleTypeName, selectedRoleType]);

  useEffect(() => {
    const fetchAssignedRoles = async () => {
      if (!userData?.user_id) {
        setAssignedRolesOptions([]);
        setSelectedAssignedRole("");
        return;
      }

      const resp = await dispatch(
        HandleApiActions({
          ...userManagementApis.get_user_assigned_roles,
          params: { user_id: userData.user_id },
          show_toast: false,
        }),
      );

      const roles = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.data)
          ? resp.data.data
          : [];
      const mappedRoles = roles
        ?.map((role) => ({
          value: role?.role_type_id,
          label: role?.display_name || role?.name,
          name: role?.name,
        }))
        ?.sort((a, b) => (a?.label || "").localeCompare(b?.label || ""));

      setAssignedRolesOptions(mappedRoles);
      const firstRole = mappedRoles?.[0];
      setSelectedAssignedRole(firstRole?.value || "");
      setSelectedAssignedRoleName(firstRole?.name || "");
    };

    fetchAssignedRoles();
  }, [dispatch, userData?.user_id]);

  useEffect(() => {
    const fetchProjectAccess = async () => {
      if (!userData?.user_id || !selectedAssignedRoleName) {
        setProjectAccessOptions([]);
        setSelectedProjectAccess("");
        return;
      }

      const resp = await dispatch(
        HandleApiActions({
          ...userManagementApis.get_user_project_access_list,
          params: {
            user_id: userData.user_id,
            role_name: selectedAssignedRoleName,
          },
          show_toast: false,
        }),
      );

      const accessList = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.data?.data)
          ? resp.data.data
          : [];

      const mappedAccess = accessList
        ?.map((access) => ({
          value: access?.access_id,
          label:
            access?.project_name ||
            (access?.project_id ? `Project ${access.project_id}` : "-"),
        }))
        ?.sort((a, b) => (a?.label || "").localeCompare(b?.label || ""));

      setProjectAccessOptions(mappedAccess);
      setSelectedProjectAccess(mappedAccess?.[0]?.value || "");
    };

    fetchProjectAccess();
  }, [dispatch, userData?.user_id, selectedAssignedRoleName]);

  const handleUserDialogClose = (confirmed) => {
    let currentTime = new Date().toISOString();

    if (confirmed) {
      setIsUserActive(pendingUserStatus);

      handleUserStatusClick({
        current_time: currentTime,
        status: pendingUserStatus,
        user_id: userData?.user_id,
      });
    }
    setOpenUserStatusDialog(false);
  };

  // useEffect(() => {
  //   let filteredRoleDepartment = rolesList
  //     ?.filter((el) => el.is_active || el.role_id === selectedRoleDepartmentId)
  //     ?.map((el) => {
  //       return {
  //         value: el.role_id,
  //         label: `${el.role_name} - ${el.department}`,
  //       };
  //     })
  //     ?.sort((a, b) => {
  //       return a.value.localeCompare(b.value);
  //     });

  //   setRoleDepartmentOptions(filteredRoleDepartment);
  // }, [rolesList, selectedRoleDepartmentId]);

  useEffect(() => {
    if (profileDetails?.profile) {
      setLoggedInUserDetails(profileDetails?.profile);
    }
  }, [profileDetails]);

  useEffect(() => {
    let filteredRolesOptions = rolesList
      ?.map((el) => {
        return { value: el.role_id, label: el.role_name };
      })
      .sort((a, b) => {
        return a.value.localeCompare(b.value);
      });

    setRolesOptions(filteredRolesOptions);
  }, [rolesList]);

  useEffect(() => {
    let filteredRegions = countryCodes
      ?.map((el) => {
        return { value: el.time_zone, label: el.time_zone };
      })
      .sort((a, b) => {
        return a.value.localeCompare(b.value);
      });

    let filteredPhoneCodes = countryCodes
      ?.map((el) => {
        return {
          value: el.phone_code,
          label: `${el.country}: ${el.phone_code}`,
          phone_code: el.phone_code,
        };
      })
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });

    setRegionOptions(filteredRegions);
    setPhoneCodeOptions(filteredPhoneCodes);
  }, [countryCodes]);

  const handleUploadProfile = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const allowedExtensions = ["jpg", "jpeg", "png"];
      if (allowedExtensions.includes(fileExtension)) {
        let newFile = new FormData();
        newFile.append("name", "filename");
        newFile.append("filename", file);

        dispatch(UploadUserPicAction({ file: newFile })).then((res) => {
          if (!res?.error) {
            if (res?.user_profile_pic_name) {
              setProfilePicName(res?.user_profile_pic_name);
            }
          } else {
          }
        });
      } else {
        toast.dark("Please select a valid image file", {
          type: "error",
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    }
  };

  useEffect(() => {
    if (profilePicName == "") {
      setProfilePicFilePath("");
    } else {
      // user_profile_pic_path

      dispatch(GetUserPicAction({ filename: profilePicName })).then((res) => {
        if (!res?.error) {
          setProfilePicFilePath(res?.user_profile_pic_path);
        } else {
        }
      });
    }
  }, [profilePicName]);

  const handleDeleteUserClick = () => {
    setOpenUserDeleteDialog(true);
  };

  const handleConfirmUserDelete = () => {
    let currentTime = new Date().toISOString();

    dispatch(
      HandleApiActions({
        ...userManagementApis.delete_user,
        params: { current_time: currentTime, user_id: userData?.user_id },
        show_toast: true,
      }),
    ).then(() => {
      handleGetUsersList();
    });

    setOpenUserDeleteDialog(false);
    setOpenDrawer(false);
  };

  const handleCancelUserDelete = () => {
    setOpenUserDeleteDialog(false);
  };

  const handleUpdateUserClick = async () => {
    let currentTime = new Date().toISOString();

    if (
      fullName !== userData?.fullname ||
      profilePicName !== userData?.profile?.profile_pic ||
      phoneNumber !== userData?.phone ||
      selectedPhoneCode !== userData?.phone_code ||
      selectedRegion !== userData?.region ||
      selectedPendingRoleDepartmentId
    ) {
      await dispatch(
        HandleApiActions({
          ...userManagementApis.update_user,
          params: {
            user_id: userData?.user_id,
            ...(userData?.fullname !== fullName && { fullname: fullName }),
            ...(userData?.profile?.profile_pic !== profilePicName && {
              profile_pic: profilePicName,
            }),
            ...(userData?.phone !== phoneNumber &&
              phoneNumber && {
              phone: { code: selectedPhoneCode, number: phoneNumber },
            }),
            ...(userData?.region !== selectedRegion && {
              region: selectedRegion,
            }),
            current_time: currentTime,
          },
        }),
      );
      handleConfirmUserUpdateRoleDepartment();
      await handleGetUsersList();
    }

    if (profileDetails?.profile?.user_id === userData?.user_id) {
      dispatch(
        HandleApiActions({
          ...profileActions.get_profile_details,
          show_toast: false,
          params: {},
        }),
      );
    }

    setOpenDrawer(false);
  };

  const handleUserStatusClick = ({ current_time, status, user_id }) => {
    dispatch(
      HandleApiActions({
        ...userManagementApis.change_user_status,
        params: { current_time, status, user_id },
        show_toast: true,
      }),
    ).then(() => {
      handleGetUsersList();
    });
  };

  const handleUserRoleChangeClick = ({ user_id, role_id, current_time }) => {
    dispatch(
      HandleApiActions({
        ...userManagementApis.change_user_role,
        params: { user_id, role_id, current_time },
        show_toast: true,
      }),
    ).then(() => {
      handleGetUsersList();
    });
  };

  console.log(
    "selectedPendingRoleDepartmentId",
    selectedPendingRoleDepartmentId,
  );

  //   const isFormValid= () => {
  //   return (
  //     !fullNameError &&
  //     fullName?.trim() === userData?.fullname?.trim() &&
  //     profilePicName === userData?.profile?.profile_pic &&
  //     phoneNumber === userData?.phone &&
  //     selectedRegion === userData?.region &&
  //     selectedPendingRoleDepartmentId === userData?.role_id
  //   );
  // };

  const isFormValid = () => {
    return (
      fullName?.trim() !== "" &&
      !fullNameError &&
      email?.trim() !== "" &&
      !emailError &&
      (phoneNumber?.trim() === "" || phoneNumber?.length === 10) &&
      selectedRoleDepartmentId &&
      selectedRegion !== ""
    );
  };
  const isUpdateFormValid = () => {
    return (
      fullName?.trim() === userData?.fullname?.trim() &&
      profilePicName === userData?.profile?.profile_pic &&
      phoneNumber === userData?.phone &&
      selectedRegion === userData?.region &&
      selectedPendingRoleDepartmentId === userData?.role_id
    );
  };

  const handleOnChangeRoleDepartment = (e) => {
    setSelectedPendingRoleDepartmentId(e.target.value);
    setSelectedRoleDepartmentId(e.target.value);
    // setOpenUserUpdateRoleDepartmentDialog(true);
  };
  console.log(
    "SelectedPendingRoleDepartmentId",
    selectedPendingRoleDepartmentId,
  );

  console.log(
    "selectedPendingRoleDepartmentIdselectedPendingRoleDepartmentId",
    selectedPendingRoleDepartmentId,
  );

  const handleConfirmUserUpdateRoleDepartment = () => {
    console.log(
      "adkaamdsadj",
      selectedPendingRoleDepartmentId,
      userData?.role_id,
    );
    console.log("userData?.role_iduserData?.role_id", userData);
    let currentTime = new Date().toISOString();

    setSelectedRoleDepartmentId(selectedPendingRoleDepartmentId);

    // if (parseInt(selectedPendingRoleDepartmentId) !== parseInt(userData?.role_id)) {
    handleUserRoleChangeClick({
      user_id: userData?.user_id,
      role_id: selectedPendingRoleDepartmentId,
      current_time: currentTime,
    });
    // }
    handleUserDialogClose(false);
    setOpenUserUpdateRoleDepartmentDialog(false);
  };

  useEffect(() => {
    customConsole("profilePicName: " + profilePicName);
    customConsole("profilePicFilePath: " + profilePicFilePath);
  }, [profilePicName, profilePicFilePath]);
  console.log("dfjkshfhsfogsfvgbovaoishnfiohnwa");

  console.log("selectedRoleDepartmentId", selectedRoleDepartmentId);

  const handleOpenProfilePicDialog = () => {
    setOpenProfilePicDialog(true);
  };
  const handleCloseProfilePicDialog = () => {
    setOpenProfilePicDialog(false);
    setSelectedProfilePic(null);
  };
  return (
    <DialogContent
      sx={{
        mt: 2,
        maxHeight: "60vh",
        overflowY: "auto",
        pr: 1,
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#b0b0b0",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#888",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f1f1f1",
          borderRadius: "8px",
        },
      }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <ProfileAvatar
            profilePicFilePath={profilePicFilePath}
            handleUploadProfile={handleUploadProfile}
          />
        </Box>

        <Box sx={styles.mainContainer}>
          <Box sx={styles.container}>
            <Stack spacing={2} sx={{ px: 2, py: 2 }}>
              <Grid container px={3} pt={2} pb={4}>
                <Grid item xs={12}>
                  <CustomFormTextFieldV2
                    size="small"
                    label={"Name"}
                    placeholder={"Name"}
                    type={"text"}
                    fldType={"text"}
                    value={fullName}
                    setValue={setFullName}
                    valueError={fullNameError}
                    setValueError={setFullNameError}
                    maxLength={80}
                    inputProps={
                      {
                        // disabled: true,
                      }
                    }
                  // txtFldStyle={styles.customTextFieldN}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomFormTextFieldV3
                    size="small"
                    label={"Email"}
                    type={"email"}
                    fldType={"email"}
                    placeholder={"Email"}
                    value={email}
                    setValue={setEmail}
                    valueError={emailError}
                    setValueError={setEmailError}
                    inputProps={{
                      disabled: true,
                    }}
                  // txtFldStyle={styles.customTextFieldN}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomSelectV3
                    size="small"
                    label="Role Type"
                    placeholder=" "
                    required={true}
                    optionsList={roleTypeOptions}
                    selectedValue={selectedRoleType}
                    handleOnChange={(e) => {
                      const newValue = e.target.value;
                      setSelectedRoleType(newValue);

                      const selectedOption = roleTypeOptions?.find(
                        (option) => String(option?.value) === String(newValue),
                      );
                      setSelectedRoleTypeName(selectedOption?.name || "");
                    }}
                    sltFldStyle={styles.customTextFieldN}
                  />
                </Grid>

                <Grid item xs={12}>
                  <CustomSelectV3
                    size={"small"}
                    label={"Organization-Role-Function"}
                    placeholder={" "}
                    optionsList={roleDepartmentOptions}
                    selectedValue={selectedRoleDepartmentId}
                    handleOnChange={handleOnChangeRoleDepartment}
                    sltFldStyle={styles.selectTextFieldN}
                  />
                </Grid>

                <Grid item xs={12}>
                  <CustomSelectV3
                    size={"small"}
                    label={"Region"}
                    optionsList={regionOptions}
                    placeholder={"Select Region"}
                    selectedValue={selectedRegion}
                    setSelectedValue={setSelectedRegion}
                    handleOnChange={(e) => setSelectedRegion(e.target.value)}
                    sltFldStyle={styles.selectTextFieldN}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PhoneNumberTextField
                    size="small"
                    phoneCodeLabel="Country"
                    phoneNumberLabel="Phone Number"
                    variant="outlined"
                    phoneCodeOptions={phoneCodeOptions}
                    selectedPhoneCode={selectedPhoneCode}
                    setSelectedPhoneCode={setSelectedPhoneCode}
                    phoneNumber={phoneNumber}
                    setPhoneNumber={setPhoneNumber}
                    phoneNumberError={phoneNumberError}
                    setPhoneNumberError={setPhoneNumberError}
                    phoneCodeStyle={{
                      width: "340px",
                    }}
                    phoneNumberStyle={{
                      width: "300px",
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "4px",
                      width: "fit-content",
                    }}
                  >
                    {/* Custom Switch UI (your existing code) */}
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: false ? "not-allowed" : "pointer",
                        userSelect: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isUserActive}
                        style={{
                          position: "absolute",
                          opacity: 0,
                          width: 50,
                          height: 26,
                          cursor: "pointer",
                          zIndex: 2,
                        }}
                        onChange={(e) =>
                          handleUserStatusChange(e.target.checked)
                        }
                      />

                      <span
                        style={{
                          display: "inline-block",
                          position: "relative",
                          width: 50,
                          height: 26,
                        }}
                      >
                        {/* Bar */}
                        <svg
                          width={50}
                          height={26}
                          style={{ position: "absolute", left: 0, top: 0 }}
                        >
                          <rect
                            x={(50 - 25) / 2}
                            y={26 / 2 - 8 / 2}
                            width={25}
                            height={8}
                            fill={isUserActive ? "#26890D" : "#53565A"}
                          />
                        </svg>

                        {/* Knob */}
                        <svg
                          width={20}
                          height={20}
                          style={{
                            position: "absolute",
                            left: isUserActive ? 50 - 20 - 3 : 3,
                            top: 3,
                            transition: "left 0.2s ease",
                          }}
                        >
                          <circle
                            cx={10}
                            cy={10}
                            r={9}
                            fill={isUserActive ? "#26890D" : "#fff"}
                            stroke={isUserActive ? "#26890D" : "#53565A"}
                            strokeWidth="2"
                          />
                          {isUserActive && (
                            <circle cx={10} cy={10} r={4} fill="#fff" />
                          )}
                        </svg>
                      </span>
                    </label>

                    {/* Status Text (unchanged) */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: isUserActive ? "#2E7D32" : "#616161",
                        // textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isUserActive ? "User Enabled" : "User Disabled"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  ></Box>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </Box>
      </Box>
      <DialogActions
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fff",
          zIndex: 20,
        }}
      >
        <CustomButtonV2
          type="cancel"
          btnName="Cancel"
          handleOnClick={() => setOpenDrawer(false)}
        />

        <CustomButtonV2
          type="submit"
          btnName="Update"
          disabled={!isFormValid() || isUpdateFormValid()}
          handleOnClick={handleUpdateUserClick}
        />
      </DialogActions>

      <CustomConfirmationDialog
        open={openUserDeleteDialog}
        onClose={handleCancelUserDelete}
        dialogTitle={"Confirm Delete"}
        dialogContent={
          <DialogContentText>
            Are you sure you want to delete this user?
          </DialogContentText>
        }
        onConfirm={handleConfirmUserDelete}
      />

      <CustomDialogBoxV2
        open={openUserUpdateRoleDepartmentDialog}
        onClose={() => setOpenUserUpdateRoleDepartmentDialog(false)}
        title={"Update User Role-Department"}
        successButtonName={"Ok"}
        rejectButtonName={"Cancel"}
        onClickSuccess={() => {
          console.log("---onclick success--");
          handleConfirmUserUpdateRoleDepartment();
        }}
        onClickReject={() => setOpenUserUpdateRoleDepartmentDialog(false)}
        onConfirm={handleConfirmUserUpdateRoleDepartment}
        actionButtonStyle={"continuous"}
      >
        <DialogContentText>
          Are you sure you want to update role-department?
        </DialogContentText>
      </CustomDialogBoxV2>
      {/* <CustomConfirmationDialog
        open={openUserStatusDialog}
        onClose={() => handleUserDialogClose(false)}
        dialogTitle={"Confirm Status Change"}
        dialogContent={
          <DialogContentText>
            Are you sure you want to change the user status to{" "}
            {pendingUserStatus ? "Enable" : "Disable"}?
          </DialogContentText>
        }
        onConfirm={() => handleUserDialogClose(true)}
      /> */}
      <CustomDialogBoxV2
        open={openUserStatusDialog}
        onClose={() => handleUserDialogClose(false)}
        title={"Confirm Status Change"}
        successButtonName={"Ok"}
        rejectButtonName={"Cancel"}
        // onClickSuccess={handleConfirmUserUpdateRoleDepartment}
        onClickSuccess={() => {
          handleUserDialogClose(true);
        }}
        onClickReject={() => handleUserDialogClose(false)}
        // dialogContent={
        //   <DialogContentText>
        //     Are you sure you want to {actionMessage}?
        //   </DialogContentText>
        // }
        // onConfirm={() => handleUserDialogClose(true)}
        actionButtonStyle={"continuous"}
      >
        <DialogContentText>
          Are you sure you want to change the user status to{" "}
          {pendingUserStatus ? "Enable" : "Disable"}?
        </DialogContentText>
      </CustomDialogBoxV2>
    </DialogContent>
  );
};

export default UpdateUser;

const styles = {
  closeIconContainer: {
    display: "flex",
    justifyContent: "flex-end",
    px: 1,
    pt: 1,
  },
  closeIcon: {
    color: "red",
  },
  imageContainer: {
    textAlign: "center",
    mt: 1,
  },
  mainContainer: {
    display: "flex",
    justifyContent: "center",
    p: 1,
  },
  container: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    p: 1,
  },
  customTextFieldN: {
    backgroundColor: "white",
    mt: 2,
  },
  selectTextFieldN: {
    mt: 2,
  },
  displayImageBox: {
    width: 150,
    height: 150,
    borderRadius: "50%",
    objectFit: "cover",
  },
  uploadImageBox: {
    backgroundColor: "none",
    height: "50px",
    width: "40px",
    borderRadius: "50%",
    marginTop: "-55px",
    marginRight: "-100px",
  },
  profileDataBox: {
    display: "flex",
    flexDirection: "row",
    gap: 2,
    alignItems: "center",
  },
  profileHeader: {
    fontWeight: "bold",
    fontSize: "16px",
    textAlign: "left",
    minWidth: "100px",
  },
  profileContent: {
    fontSize: "16px",
    textAlign: "right",
    marginLeft: "15px",
  },
  userStatusContainer: {
    width: "100%",
    mt: 2,
  },
  toggleBtnGroup: {
    borderRadius: "20px",
    backgroundColor: "lightgrey",
  },
  deleteBtn: {
    // width: "100%",
    width: "140px",
    mt: 2,
    ...appTheme.commonBtnStyle,
    // borderRadius: "0px",
    //bgcolor: "#f44336",
    // "&:hover": {
    //   bgcolor: "#d32f2f",
    // },
  },
  updateBtn: {
    width: "140px",
    ...appTheme.commonBtnStyle,
    mt: 2,
  },
};

function CommonInputSwitch({
  checked,
  onChange,
  enabledText = "User Enabled",
  disabledText = "User Disabled",
  disabled,
}) {
  const GREEN = "#2E7D32";
  const GREY = "#9E9E9E";

  const SWITCH_WIDTH = 36;
  const SWITCH_HEIGHT = 20;
  const KNOB_SIZE = 16;
  const KNOB_MARGIN = 2;

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "4px 12px",
        borderRadius: "4px",
        width: "fit-content",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {/* Hidden input */}
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        style={{
          position: "absolute",
          opacity: 0,
          width: SWITCH_WIDTH,
          height: SWITCH_HEIGHT,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      />

      {/* Switch */}
      <span
        style={{
          position: "relative",
          width: SWITCH_WIDTH,
          height: SWITCH_HEIGHT,
        }}
      >
        {/* Track */}
        <div
          style={{
            position: "absolute",
            top: SWITCH_HEIGHT / 2 - 6,
            left: 0,
            width: SWITCH_WIDTH,
            height: 12,
            borderRadius: "6px",
            backgroundColor: checked ? GREEN : GREY,
            transition: "background-color 0.2s ease",
          }}
        />

        {/* Thumb */}
        <div
          style={{
            position: "absolute",
            top: KNOB_MARGIN,
            left: checked
              ? SWITCH_WIDTH - KNOB_SIZE - KNOB_MARGIN
              : KNOB_MARGIN,
            width: KNOB_SIZE,
            height: KNOB_SIZE,
            borderRadius: "50%",
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            transition: "left 0.2s ease",
          }}
        />
      </span>

      {/* Status Text */}
      <span
        style={{
          fontWeight: 600,
          fontSize: "12px",
          color: checked ? GREEN : "#616161",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {checked ? enabledText : disabledText}
      </span>
    </div>
  );
}

function ProfileAvatar({ profilePicFilePath, handleUploadProfile }) {
  const fileInputRef = useRef(null);

  const handleIconClick = () => {
    fileInputRef.current.click(); // trigger file input
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-block",
      }}
    >
      <Avatar
        sx={{
          width: 128,
          height: 128,
          bgcolor: "#E0E0E0",
          border: "1px solid #ddd",
        }}
        src={profilePicFilePath ? profilePicFilePath : DefaultProfilePic}
      />

      <IconButton
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          transform: "translate(25%, 25%)",
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          width: 32,
          height: 32,
          boxShadow: 1,
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
        }}
        onClick={handleIconClick}
      >
        <BorderColorIcon sx={{ color: "#000", fontSize: 16 }} />
      </IconButton>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        accept="image/*"
        hidden
        type="file"
        onChange={handleUploadProfile}
      />
    </Box>
  );
}
